import { useMemo } from 'react';
import type { FerryCredential, FerryRoute } from '@my/ferry-api';
import { useFerryCarriers, useFerryCredentials, useFerryRoutes } from '@my/ferry-api/hooks';
import {
  ferryCarrierCodeFor,
  ferryRouteCodeFor,
  type CrossingCarrier,
  type CrossingRoute,
  type FerryVehicleTypeMap,
} from './catalogue';

/**
 * What My.Ferry knows about a picked crossing: whether the carrier is booked
 * through Ferry at all, and if so the ids, the port clocks and the
 * organisation's accounts the booking needs.
 */
export interface FerryCrossing {
  /** The crossing carries a Ferry code, so its carrier is booked through My.Ferry. */
  linked: boolean;
  /** Everything a booking needs is in place; the form shows the Ferry fields. */
  enabled: boolean;
  isLoading: boolean;
  /** Why a linked crossing cannot be booked through Ferry right now; the booking then stays manual. */
  unavailableReason: string | null;
  carrierId: string | null;
  carrierName: string;
  routeCode: string | null;
  route: FerryRoute | null;
  /** IANA zones of the two ports; UTC until the route is known. */
  originZone: string;
  destinationZone: string;
  /** The organisation's active accounts at the carrier. */
  credentials: FerryCredential[];
  /** More than one account: the booking must say which. */
  credentialRequired: boolean;
  /** The carrier file's vehicle type per TMS booking type; null when it has none. */
  vehicleTypes: FerryVehicleTypeMap | null;
}

const NOT_LINKED: FerryCrossing = {
  linked: false,
  enabled: false,
  isLoading: false,
  unavailableReason: null,
  carrierId: null,
  carrierName: '',
  routeCode: null,
  route: null,
  originZone: 'UTC',
  destinationZone: 'UTC',
  credentials: [],
  credentialRequired: false,
  vehicleTypes: null,
};

/** A route entered by hand in Ferry: no timetable, no carrier call. Picked like any other, booked by hand. */
const MANUAL: FerryCrossing = {
  ...NOT_LINKED,
  linked: true,
  unavailableReason: 'This crossing is booked by hand at the carrier; the booking stays manual.',
};

/**
 * A pick is found at Ferry by the codes the file links it with (a pick that
 * already carries its route and carrier id skips the lookup). With `enabled`
 * false (the Integration switch Off) Ferry is asked nothing: the crossing is
 * reported linked but not enabled, and the booking stays a TMS one.
 */
export function useFerryCrossing(
  organisationId: string | null,
  entry: CrossingRoute | null | undefined,
  reversed: boolean,
  carrier: CrossingCarrier | null | undefined,
  enabled = true,
): FerryCrossing {
  const routeCode = ferryRouteCodeFor(entry, reversed);
  const carrierCode = ferryCarrierCodeFor(carrier);
  const linked = !!routeCode && !!carrierCode;
  const manual = !!entry?.ferryRoute?.isManual;
  const asking = enabled && linked && !manual;
  const carrierName = carrier?.name ?? 'The carrier';
  const vehicleTypes = carrier?.ferryVehicleTypes ?? null;
  const pickedRoute = entry?.ferryRoute ?? null;
  const pickedCarrierId = carrier?.ferryCarrierId ?? null;

  const carriersQuery = useFerryCarriers({ enabled: asking && !pickedCarrierId });
  const ferryCarrier = useMemo(
    () =>
      pickedCarrierId
        ? null
        : (carriersQuery.data?.find((c) => c.code.toUpperCase() === carrierCode?.toUpperCase()) ??
          null),
    [pickedCarrierId, carriersQuery.data, carrierCode],
  );
  const carrierId = linked ? (pickedCarrierId ?? ferryCarrier?.id ?? null) : null;

  const routesQuery = useFerryRoutes(!asking || pickedRoute ? null : carrierId);
  const credentialsQuery = useFerryCredentials(
    asking ? organisationId : null,
    carrierId ?? undefined,
  );

  return useMemo<FerryCrossing>(() => {
    if (!linked) return NOT_LINKED;
    if (manual) return MANUAL;
    if (!enabled) return { ...NOT_LINKED, linked: true, carrierName, routeCode, vehicleTypes };

    const route = pickedRoute ?? routesQuery.data?.find((r) => r.code === routeCode) ?? null;
    if (route?.isManual) return MANUAL;
    const credentials = (credentialsQuery.data ?? []).filter(
      (c) => c.status === 'Active' && (!carrierId || c.carrierId === carrierId),
    );
    const lookingUpCarrier = !pickedCarrierId && carriersQuery.isPending;
    const lookingUpRoute = !pickedRoute && !!carrierId && routesQuery.isPending;
    const isLoading =
      lookingUpCarrier || lookingUpRoute || (!!carrierId && credentialsQuery.isPending);

    let unavailableReason: string | null = null;
    if (carriersQuery.isError || routesQuery.isError || credentialsQuery.isError) {
      unavailableReason = 'The ferry service could not be reached; the booking stays manual.';
    } else if (!pickedCarrierId && carriersQuery.data && !ferryCarrier) {
      unavailableReason = `${carrierName} is not known to the ferry service; the booking stays manual.`;
    } else if (!pickedRoute && routesQuery.data && !route) {
      unavailableReason = `Route ${routeCode} is not known to the ferry service; the booking stays manual.`;
    } else if (route && !route.isActive) {
      unavailableReason = `Route ${routeCode} is no longer offered by ${carrierName}; the booking stays manual.`;
    } else if (credentialsQuery.data && credentials.length === 0) {
      unavailableReason = `No ${carrierName} account is configured for this organisation; the booking stays manual.`;
    }

    return {
      linked,
      enabled: !isLoading && !unavailableReason && !!carrierId && !!route,
      isLoading,
      unavailableReason,
      carrierId,
      carrierName,
      routeCode,
      route,
      originZone: route?.originTimeZoneId || 'UTC',
      destinationZone: route?.destinationTimeZoneId || 'UTC',
      credentials,
      credentialRequired: credentials.length > 1,
      vehicleTypes,
    };
  }, [
    linked,
    manual,
    enabled,
    routeCode,
    carrierId,
    carrierName,
    vehicleTypes,
    pickedRoute,
    pickedCarrierId,
    ferryCarrier,
    carriersQuery.data,
    carriersQuery.isPending,
    carriersQuery.isError,
    routesQuery.data,
    routesQuery.isPending,
    routesQuery.isError,
    credentialsQuery.data,
    credentialsQuery.isPending,
    credentialsQuery.isError,
  ]);
}
